import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RESOURCE_KEYS, TRAIT_DEFS } from '@pk/shared';
import type { CharacterSummary, Id, Resources, TitleRank, TitleSummary } from '@pk/shared';
import { useCharacter, useHouse, useHouseDetail, useTitleIndex, useWorld } from '../api/hooks';
import { MS_PER_GAME_HOUR, formatDateTime } from '@pk/shared';
import { epochMsOf, formatEta, msUntilAbsHour, msUntilDay } from '../lib/eta';
import { Portrait } from './Portrait';
import { Heraldry } from './Heraldry';
import { Tag } from './ui';
import { ResourceIcon } from './icons';

/** Rank held (not knighthood) for a character, via the world title index. */
export function heldRank(c: Pick<CharacterSummary, 'id' | 'primaryTitleId'>, titles: Map<Id, TitleSummary>): TitleRank | null {
  if (!c.primaryTitleId) return null;
  const t = titles.get(c.primaryTitleId);
  return t && t.holderId === c.id ? t.rank : null;
}

export function CharPortrait({ c, size = 56 }: { c: CharacterSummary; size?: number }) {
  const titles = useTitleIndex();
  return <Portrait genes={c.faceGenes} sex={c.sex} age={c.age} alive={c.alive} rank={heldRank(c, titles)} size={size} />;
}

/** Inline link to a character; resolves the name through the query cache. */
export function CharLink({ id, fallback }: { id: Id | null | undefined; fallback?: string }) {
  const { data } = useCharacter(id);
  const { t } = useTranslation();
  if (!id) return <span className="muted">{fallback ?? t('common.none')}</span>;
  return <Link to={`/character/${id}`} className="link">{data?.titledName ?? data?.name ?? data?.firstName ?? fallback ?? id}</Link>;
}

export function TitleLink({ id, fallback }: { id: Id | null | undefined; fallback?: string }) {
  const titles = useTitleIndex();
  const { t } = useTranslation();
  if (!id) return <span className="muted">{fallback ?? t('common.none')}</span>;
  return <Link to={`/title/${id}`} className="link">{titles.get(id)?.name ?? id}</Link>;
}

export function TraitTags({ traits, max = 3, hideEducation = false }: { traits: string[]; max?: number; hideEducation?: boolean }) {
  const list = traits.filter((k) => TRAIT_DEFS[k] && !(hideEducation && TRAIT_DEFS[k].category === 'education'));
  const sorted = [...list].sort((a, b) => order(TRAIT_DEFS[a].category) - order(TRAIT_DEFS[b].category)).slice(0, max);
  return (
    <span className="tags">
      {sorted.map((k) => {
        const d = TRAIT_DEFS[k];
        const tone = d.category === 'congenital' ? 'blue' : d.category === 'acquired' ? 'red' : d.category === 'education' ? 'green' : 'plain';
        return <Tag key={k} tone={tone} title={d.description}>{d.name}</Tag>;
      })}
    </span>
  );
}
function order(c: string) { return ({ personality: 0, congenital: 1, education: 2, acquired: 3 } as Record<string, number>)[c] ?? 4; }

export function MemberCard({ c, extra }: { c: CharacterSummary; extra?: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <Link to={`/character/${c.id}`} className="member member-row">
      <CharPortrait c={c} size={52} />
      <div className="member-body">
        <div className="member-name">{c.titledName ?? c.name}{c.isHead && <span className="crest-mark" title={t('character.head')}> {t('character.headShort')}</span>}</div>
        <div className="muted small">{c.alive ? t('common.age', { age: c.age }) : t('character.dead')}{c.primaryTitle ? ` · ${c.primaryTitle}` : ''}</div>
        <TraitTags traits={c.traits} max={2} hideEducation />
        {extra}
      </div>
    </Link>
  );
}

/** Compact number: 12 -> "12", 12345 -> "12.3k". */
export function formatAmount(n: number): string {
  const v = Math.floor(n);
  return Math.abs(v) >= 10000 ? `${(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k` : String(v);
}
/** Signed monthly balance: "+12", "-3.5", "0". */
export function formatBalance(b: number): string {
  return Math.abs(b) < 0.05 ? '0' : `${b > 0 ? '+' : ''}${b.toFixed(Math.abs(b) < 10 ? 1 : 0)}`;
}

/** Grid of all resources: colour icon, name, amount and (if `balance` given) the coloured monthly change. */
export function ResourceChips({ resources, balance }: { resources: Resources; balance?: Resources }) {
  const { t } = useTranslation();
  return (
    <div className="resources">
      {RESOURCE_KEYS.map((k) => {
        const b = balance?.[k];
        return (
          <div key={k} className="res">
            <span className="res-ico"><ResourceIcon k={k} /></span>
            <span className="res-name">{t(`resource.${k}`)}</span>
            <span className="res-val num">{Math.floor(resources[k])}</span>
            {b !== undefined && (
              <span className={`res-bal num ${b > 0.05 ? 'pos' : b < -0.05 ? 'neg' : ''}`} title={t('house.perMonth')}>
                {b > 0.05 ? '\u25B2 ' : b < -0.05 ? '\u25BC ' : ''}{formatBalance(b)}{t('house.perMonthShort')}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function costText(cost: Partial<Resources>, mult: number, t: (k: string) => string): string {
  const parts = RESOURCE_KEYS.filter((k) => (cost[k] ?? 0) > 0).map((k) => `${(cost[k] ?? 0) * mult} ${t(`resource.${k}`).toLowerCase()}`);
  return parts.join(', ') || t('common.free');
}
export function canAfford(have: Resources, cost: Partial<Resources>, mult: number): boolean {
  return RESOURCE_KEYS.every((k) => have[k] >= (cost[k] ?? 0) * mult);
}

export function HouseBadge({ houseId, name }: { houseId: Id | null; name?: string | null }) {
  const { data } = useWorld();
  const h = data?.houses.find((x) => x.id === houseId);
  if (!houseId) return null;
  return (
    <span className="housebadge">
      {h && <Heraldry seed={h.heraldrySeed} size={16} />}
      <span>{name ?? h?.name ?? houseId}</span>
    </span>
  );
}

export function Countdown({ targetMs }: { targetMs: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const left = Math.max(0, targetMs - now);
  const s = Math.ceil(left / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return <span className="num">{mm}:{ss}</span>;
}

/**
 * Pick a character of another house: choose a house, then one of its members.
 * `filter` narrows which members are selectable (e.g. unmarried adults of the other sex).
 */
export function CharacterPicker({ value, onChange, filter, excludeHouseId, label }: {
  value: Id | null;
  onChange: (id: Id | null, c?: CharacterSummary) => void;
  filter?: (c: CharacterSummary) => boolean;
  excludeHouseId?: Id | null;
  label?: string;
}) {
  const { t } = useTranslation();
  const { data: world } = useWorld();
  const [houseId, setHouseId] = useState<Id | null>(null);
  const [q, setQ] = useState('');
  const detail = useHouseDetail(houseId);
  const houses = useMemo(() => {
    const all = (world?.houses ?? []).filter((h) => !h.extinct && h.id !== excludeHouseId);
    const needle = q.trim().toLowerCase();
    return (needle ? all.filter((h) => h.name.toLowerCase().includes(needle)) : all).sort((a, b) => b.prestige - a.prestige).slice(0, 40);
  }, [world, q, excludeHouseId]);
  const members = (detail.data?.members ?? []).filter((m) => m.alive && (filter ? filter(m) : true));

  return (
    <div className="picker">
      {label && <div className="field-label">{label}</div>}
      {!houseId ? (
        <>
          <input className="input" type="search" placeholder={t('picker.searchHouse')} value={q} onChange={(e) => setQ(e.target.value)} />
          <ul className="picklist">
            {houses.map((h) => (
              <li key={h.id}>
                <button type="button" className="pick" onClick={() => setHouseId(h.id)}>
                  <Heraldry seed={h.heraldrySeed} size={20} />
                  <span>{t('picker.house', { name: h.name })}</span>
                  <span className="muted small">{h.highestRank === 'landless' ? t('common.landless') : t(`rank.${h.highestRank}`)}</span>
                </button>
              </li>
            ))}
            {houses.length === 0 && <li className="empty">{t('picker.noHouses')}</li>}
          </ul>
        </>
      ) : (
        <>
          <button type="button" className="link back" onClick={() => { setHouseId(null); onChange(null); }}>{t('picker.changeHouse')}</button>
          {detail.isLoading && <p className="muted">{t('common.loading')}</p>}
          <ul className="picklist">
            {members.map((m) => (
              <li key={m.id}>
                <button type="button" className={`pick ${value === m.id ? 'on' : ''}`} onClick={() => onChange(m.id, m)}>
                  <CharPortrait c={m} size={28} />
                  <span>{m.firstName}</span>
                  <span className="muted small">{t('common.age', { age: m.age })}{m.primaryTitle ? ` · ${m.primaryTitle}` : ''}</span>
                </button>
              </li>
            ))}
            {!detail.isLoading && members.length === 0 && <li className="empty">{t('picker.noMembers')}</li>}
          </ul>
        </>
      )}
    </div>
  );
}


/** Returns a formatter: game day -> "~12 min" (real time until that day starts). */
export function useEta(): (day: number) => string {
  const { t } = useTranslation();
  const { data: w } = useWorld();
  return (day: number) => (w ? formatEta(msUntilDay(w, day), t as never) : '');
}

/** Formatter for absolute in-game hours: `when` = "14 September 1010 · 06:00", `eta` = "~2 h", `full` = both. */
export function useHourEta(): (absHour: number) => { when: string; eta: string; full: string } {
  const { t } = useTranslation();
  const { data: w } = useWorld();
  const epoch = w ? epochMsOf(w) : 0;
  return (absHour: number) => {
    if (!w) return { when: '', eta: '', full: '' };
    const when = formatDateTime(absHour);
    const eta = formatEta(msUntilAbsHour(absHour, epoch), t as never);
    return { when, eta, full: `${when} (${eta})` };
  };
}

/** Live in-game clock: the current hour of the game day, "· 14:00" (ticks by itself). */
export function GameHour({ epochMs }: { epochMs: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const h = ((Math.floor((now - epochMs) / MS_PER_GAME_HOUR) % 24) + 24) % 24;
  return <span className="num">· {String(h).padStart(2, '0')}:00</span>;
}

/** "AI" / "You" / "Away" marker for the house with this id. */
export function HouseTag({ houseId }: { houseId: Id | null | undefined }) {
  const { t } = useTranslation();
  const { data: world } = useWorld();
  const { data: house } = useHouse();
  if (!houseId) return null;
  if (house?.house.id === houseId) return <Tag tone="gold">{t('common.you')}</Tag>;
  const h = world?.houses.find((x) => x.id === houseId);
  if (!h) return null;
  if (h.control === 'caretaker') return <Tag tone="blue" title={t('common.awayHint')}>{t('common.away')}</Tag>;
  if (h.control === 'ai') return <Tag title={h.wasPlayer ? t('common.formerPlayer') : t('common.aiHint')}>{h.wasPlayer ? t('common.aiFormer') : t('common.ai')}</Tag>;
  return null;
}

/** Character link + house badge + AI marker, on one line. */
export function HolderLine({ id, houseId, name }: { id: Id | null; houseId: Id | null; name?: string | null }) {
  const { t } = useTranslation();
  if (!id) return <span className="muted">{t('common.vacant')}</span>;
  return (
    <span className="holder">
      <CharLink id={id} fallback={name ?? undefined} /> <HouseTag houseId={houseId} />
    </span>
  );
}
